import api from 'core/util/api';
import { MCP_OAUTH_BROADCAST_CHANNEL, MCP_OAUTH_STATE_TTL_SECONDS } from '@kentik/ui-shared/kaizen/constants';

const POPUP_TIMEOUT_MS = MCP_OAUTH_STATE_TTL_SECONDS * 1000;
const POPUP_FEATURES = 'width=600,height=700';

// A 4xx from our authorize endpoint carries our own reason and what to do next; anything else stays generic.
function authorizeRefusal(err) {
  const refusal = err?.status >= 400 && err.status < 500 ? err.body?.error : null;

  return refusal ? Object.assign(new Error(refusal), { refusal }) : err;
}

export function openMcpOAuthPopup(
  serverId,
  { onBlocked, pause, authorizePath = `/api/ui/ai/mcp-servers/${serverId}/oauth/authorize` } = {}
) {
  const popup = window.open('about:blank', `mcp-oauth-${serverId}`, POPUP_FEATURES);

  return new Promise((resolve, reject) => {
    const bc = new BroadcastChannel(MCP_OAUTH_BROADCAST_CHANNEL);
    let expectedState = null;
    let pollClosed;
    let timeout;

    const closePopup = () => {
      try {
        if (popup && !popup.closed) {
          popup.close();
        }
      } catch (e) {
        // COOP blocks access to the popup handle on cross-origin pages
      }
    };

    const cleanup = () => {
      clearTimeout(timeout);
      clearInterval(pollClosed);
      bc.close();
    };

    timeout = setTimeout(() => {
      cleanup();
      closePopup();
      const timedOut = new Error('Authentication timed out');
      timedOut.timedOut = true;
      reject(timedOut);
    }, POPUP_TIMEOUT_MS);

    bc.onmessage = (event) => {
      const data = event.data || {};

      if (!expectedState || data.state !== expectedState) {
        return;
      }

      if (data.type === 'mcp-oauth-complete') {
        cleanup();
        resolve({ connected: true });
      } else if (data.type === 'mcp-oauth-cancelled') {
        cleanup();
        const cancelled = new Error('Sign-in was not completed');
        cancelled.cancelled = true;
        reject(cancelled);
      } else if (data.type === 'mcp-oauth-error') {
        cleanup();
        reject(new Error());
      }
    };

    api
      .post(authorizePath, {
        data: { pause },
        showErrorToast: false,
        suppressNotFoundErrors: true,
        rawResponse: true
      })
      .then(({ body: { authorize_url, state } }) => {
        expectedState = state;

        if (popup && !popup.closed) {
          // Severs the provider page's handle on this tab before it can navigate us anywhere.
          try {
            popup.opener = null;
          } catch (e) {
            // COOP already isolated the popup
          }

          popup.location.href = authorize_url;

          return;
        }

        // No window to drive, so hand the URL back and keep listening for the user to finish it.
        if (onBlocked) {
          onBlocked(authorize_url);

          return;
        }

        cleanup();
        reject(new Error('Popup was blocked'));
      })
      .catch((err) => {
        cleanup();
        closePopup();
        reject(authorizeRefusal(err));
      });

    pollClosed = setInterval(() => {
      try {
        if (popup && popup.closed) {
          cleanup();
          const cancelled = new Error('Popup closed');
          cancelled.cancelled = true;
          reject(cancelled);
        }
      } catch (e) {
        // COOP blocks window.closed on cross-origin popups
      }
    }, 1000);
  });
}
